import { FormSelect } from "../../../components/ui/select";
import i18n from "../../../i18n";
import { Trans } from "react-i18next";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { Button } from "../../../components/ui/button";
import { AlertBanner, PageHeader } from "../../../components/ui";
import { customerService } from "../../../services/customerService";
import { AMC_TYPE_OPTIONS, ASSET_STATUS_OPTIONS, WARRANTY_TYPE_OPTIONS } from "../constants";
import {
  assetMissingRequired,
  assetRecordId,
  buildAssetPayload,
  customerLabel,
  customerRecordId,
  emptyAsset,
  fetchCustomerChoices,
  fetchCustomerLocations,
  isNoAmcType,
  locationLabel,
  locationRecordId,
  mapAssetFromApi,
  NO_AMC_TYPE,
  withSelectedCustomer
} from "./assetFormModel";

function FieldLabel({ i18nKey, className, required }) {
  const raw = String(i18n.t(i18nKey));
  const text = raw.replace(/\s*\*\s*$/, "").trimEnd();
  const showRequired = required !== undefined ? Boolean(required) : /\*\s*$/.test(raw);
  return (
    <label className={className}>
      {text}
      {showRequired ? <span className="text-rose-500"> *</span> : null}
    </label>
  );
}

function translateAmcTypeLabel(type) {
  const map = {
    Comprehensive: "crm.comprehensive",
    "Non Comprehensive": "crm.nonComprehensive",
    "No AMC": "crm.noAmc"
  };
  return map[type] ? i18n.t(map[type]) : type;
}

function translateWarrantyTypeLabel(type) {
  if (type === "Yes") return i18n.t("common.yes");
  if (type === "No") return i18n.t("common.no");
  return type;
}

function translateAssetStatusLabel(status) {
  if (status === "Active") return i18n.t("status.active");
  if (status === "Inactive") return i18n.t("status.inactive");
  return status;
}

const fieldClass = "h-9 rounded-lg border border-(--color-text-border) px-2.5 text-xs bg-white focus:outline-hidden";
const selectClass = "h-9 rounded-lg border border-(--color-text-border) px-2 text-xs bg-white focus:outline-hidden disabled:opacity-60";
const ASSETS_LIST_PATH = "/services/CRM/Customer/Customer-Assets";

function AssetFields({
  asset,
  index,
  locations,
  categoryOptions,
  lookupsLoading,
  canRemove,
  onChange,
  onRemove
}) {
  const assetId = assetRecordId(asset);
  const warrantyValue = WARRANTY_TYPE_OPTIONS.includes(asset.warrantyType) ? asset.warrantyType : "";
  const statusValue = (() => {
    const raw = asset.assetStatus || "Active";
    if (String(raw).toLowerCase() === "inactive") return "Inactive";
    return ASSET_STATUS_OPTIONS.includes(raw) ? raw : "Active";
  })();

  return (
    <div className="p-4 border border-(--color-text-border)/60 rounded-xl space-y-3 bg-workspace/10">
      <div className="flex justify-between items-center">
        <span className="text-xs font-bold text-primary uppercase">
          {i18n.t("crm.asset")}
          {index + 1}
          {assetId ? `${i18n.t("crm.idPrefix")}${assetId}` : i18n.t("crm.newSuffix")}
        </span>
        {canRemove && (
          <Button type="button" variant="ghost" onClick={onRemove} className="h-7 w-7 p-0 text-error hover:bg-rose-50">
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="flex flex-col gap-1">
          <FieldLabel className="text-[11px] font-bold per-file-hint" i18nKey="common.location2" />
          <FormSelect required value={String(asset.locationId || "")} onChange={(e) => onChange({ locationId: e.target.value })} className={selectClass}>
            <option value="">{i18n.t("common.selectLocation")}</option>
            {locations.map((location) => {
              const id = locationRecordId(location);
              return (
                <option key={id} value={String(id)}>{locationLabel(location)}</option>
              );
            })}
          </FormSelect>
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel className="text-[11px] font-bold per-file-hint" i18nKey="crm.assetName" />
          <input required type="text" value={asset.assetName || ""} onChange={(e) => onChange({ assetName: e.target.value })} className={fieldClass} />
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel className="text-[11px] font-bold per-file-hint" i18nKey="common.category2" />
          <FormSelect
            required
            value={asset.assetCategory || ""}
            onChange={(e) => onChange({ assetCategory: e.target.value })}
            disabled={lookupsLoading && categoryOptions.length === 0}
            className={selectClass}
          >
            <option value="">{lookupsLoading && categoryOptions.length === 0 ? i18n.t("crm.loadingCategories") : i18n.t("onboarding.selectCategory")}</option>
            {categoryOptions.map((opt) => (
              <option key={`${opt.id}-${opt.label}`} value={opt.label}>{opt.label}</option>
            ))}
            {asset.assetCategory && !categoryOptions.some((opt) => opt.label === asset.assetCategory) && (
              <option value={asset.assetCategory}>{asset.assetCategory}</option>
            )}
          </FormSelect>
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel className="text-[11px] font-bold per-file-hint" i18nKey="crm.manufacturer" required={false} />
          <input type="text" value={asset.manufacturer || ""} onChange={(e) => onChange({ manufacturer: e.target.value })} className={fieldClass} />
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel className="text-[11px] font-bold per-file-hint" i18nKey="crm.modelNumber" required={false} />
          <input type="text" value={asset.modelNumber || ""} onChange={(e) => onChange({ modelNumber: e.target.value })} className={fieldClass} />
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel className="text-[11px] font-bold per-file-hint" i18nKey="crm.serialNumber" />
          <input type="text" value={asset.serialNumber || ""} onChange={(e) => onChange({ serialNumber: e.target.value })} className={fieldClass} />
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel className="text-[11px] font-bold per-file-hint" i18nKey="inventory.invoiceNumber" required={false} />
          <input type="text" maxLength={50} value={asset.invoiceNumber ?? ""} onChange={(e) => onChange({ invoiceNumber: e.target.value })} className={fieldClass} />
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel className="text-[11px] font-bold per-file-hint" i18nKey="dateTime.invoiceDate" required={false} />
          <input type="date" value={asset.invoiceDate || ""} onChange={(e) => onChange({ invoiceDate: e.target.value })} className={fieldClass} />
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel className="text-[11px] font-bold per-file-hint" i18nKey="dateTime.installDate" required={false} />
          <input type="date" value={asset.installDate || ""} onChange={(e) => onChange({ installDate: e.target.value })} className={fieldClass} />
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel className="text-[11px] font-bold per-file-hint" i18nKey="crm.warrantyType" required={false} />
          <FormSelect value={warrantyValue} onChange={(e) => onChange({ warrantyType: e.target.value })} className={selectClass}>
            <option value="">{i18n.t("crm.selectWarrantyType")}</option>
            {WARRANTY_TYPE_OPTIONS.map((type) => (
              <option key={type} value={type}>{translateWarrantyTypeLabel(type)}</option>
            ))}
          </FormSelect>
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel className="text-[11px] font-bold per-file-hint" i18nKey="dateTime.warrantyStartDate" />
          <input type="date" value={asset.warrantyStartDate || ""} onChange={(e) => onChange({ warrantyStartDate: e.target.value })} className={fieldClass} />
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel className="text-[11px] font-bold per-file-hint" i18nKey="dateTime.warrantyEndDate" required={false} />
          <input
            type="date"
            value={asset.warrantyExpiry || asset.warrantyEndDate || ""}
            onChange={(e) => onChange({ warrantyExpiry: e.target.value, warrantyEndDate: e.target.value })}
            className={fieldClass}
          />
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel className="text-[11px] font-bold per-file-hint" i18nKey="crm.amcType" required={false} />
          <FormSelect
            value={asset.amcType || ""}
            onChange={(e) => {
              const nextType = e.target.value;
              if (nextType === NO_AMC_TYPE) {
                onChange({
                  amcType: nextType,
                  amcOrder: "",
                  amcOrderDate: "",
                  AMCStartDate: "",
                  AMCEndDate: ""
                });
                return;
              }
              onChange({ amcType: nextType });
            }}
            className={selectClass}
          >
            <option value="">{i18n.t("crm.selectAmcType")}</option>
            {AMC_TYPE_OPTIONS.map((type) => (
              <option key={type} value={type}>{translateAmcTypeLabel(type)}</option>
            ))}
            {asset.amcType && !AMC_TYPE_OPTIONS.includes(asset.amcType) && (
              <option value={asset.amcType}>{asset.amcType}</option>
            )}
          </FormSelect>
        </div>
        {!isNoAmcType(asset) && (
          <div className="flex flex-col gap-1">
            <FieldLabel className="text-[11px] font-bold per-file-hint" i18nKey="crm.amcOrder" required={false} />
            <input type="text" value={asset.amcOrder || ""} onChange={(e) => onChange({ amcOrder: e.target.value })} className={fieldClass} />
          </div>
        )}
        {!isNoAmcType(asset) && (
          <div className="flex flex-col gap-1">
            <FieldLabel className="text-[11px] font-bold per-file-hint" i18nKey="crm.amcOrderDate" required={false} />
            <input type="date" value={asset.amcOrderDate || ""} onChange={(e) => onChange({ amcOrderDate: e.target.value })} className={fieldClass} />
          </div>
        )}
        <div className="flex flex-col gap-1">
          <FieldLabel className="text-[11px] font-bold per-file-hint" i18nKey="crm.tenantAssetId" required={false} />
          <input type="text" value={asset.tenantAssetId || ""} onChange={(e) => onChange({ tenantAssetId: e.target.value })} className={fieldClass} />
        </div>
        {!isNoAmcType(asset) && (
          <div className="flex flex-col gap-1">
            <FieldLabel className="text-[11px] font-bold per-file-hint" i18nKey="dateTime.amcStartDate" required={false} />
            <input type="date" value={asset.AMCStartDate || ""} onChange={(e) => onChange({ AMCStartDate: e.target.value })} className={fieldClass} />
          </div>
        )}
        {!isNoAmcType(asset) && (
          <div className="flex flex-col gap-1">
            <FieldLabel className="text-[11px] font-bold per-file-hint" i18nKey="dateTime.amcEndDate" required={false} />
            <input type="date" value={asset.AMCEndDate || ""} onChange={(e) => onChange({ AMCEndDate: e.target.value })} className={fieldClass} />
          </div>
        )}
        <div className="flex flex-col gap-1">
          <FieldLabel className="text-[11px] font-bold per-file-hint" i18nKey="crm.assetStatus" required={false} />
          <FormSelect value={statusValue} onChange={(e) => onChange({ assetStatus: e.target.value })} className={selectClass}>
            {ASSET_STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>{translateAssetStatusLabel(status)}</option>
            ))}
          </FormSelect>
        </div>
      </div>
    </div>
  );
}

export function CustomerAssetFormPage() {
  const navigate = useNavigate();
  const { assetId } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(assetId);
  const [customerId, setCustomerId] = useState(() => searchParams.get("customerId") || "");
  const [customerName, setCustomerName] = useState("");
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [locations, setLocations] = useState([]);
  const [assets, setAssets] = useState([emptyAsset()]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [lookupsLoading, setLookupsLoading] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [formWarning, setFormWarning] = useState("");

  const listPath = customerId
    ? `${ASSETS_LIST_PATH}?customerId=${encodeURIComponent(customerId)}`
    : ASSETS_LIST_PATH;

  useEffect(() => {
    let active = true;
    setLookupsLoading(true);
    customerService.getAssetCategoryOptions().then((options) => {
      if (active) setCategoryOptions(options || []);
    }).catch((err) => {
      console.error("Failed to load asset categories:", err);
    }).finally(() => {
      if (active) setLookupsLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (isEdit) return undefined;
    let active = true;
    fetchCustomerChoices("").then((list) => {
      if (active) setCustomers(list);
    }).catch((err) => {
      console.error("Failed to load customers:", err);
    });
    return () => {
      active = false;
    };
  }, [isEdit]);

  useEffect(() => {
    if (!customerId) {
      setLocations([]);
      if (!isEdit) setAssets([emptyAsset()]);
      return undefined;
    }
    let active = true;
    fetchCustomerLocations(customerId).then((list) => {
      if (!active) return;
      setLocations(list);
      if (!isEdit) {
        const firstId = locationRecordId(list[0]) || "";
        setAssets((prev) => {
          const started = prev.some((asset) => assetRecordId(asset) || (asset.assetName || "").trim() || (asset.assetCategory || "").trim());
          if (started) {
            return prev.map((asset) => (asset.locationId ? asset : { ...asset, locationId: String(firstId) }));
          }
          return [emptyAsset(firstId)];
        });
      }
    }).catch((err) => {
      console.error("Failed to load locations:", err);
      if (active) setLocations([]);
    });
    return () => {
      active = false;
    };
  }, [customerId, isEdit]);

  useEffect(() => {
    if (!customerId) return undefined;
    let active = true;
    customerService.getCustomerById(customerId).then((customer) => {
      if (!active || !customer) return;
      setCustomerName(customerLabel(customer));
      setSelectedCustomer(customer);
    }).catch(() => {});
    return () => {
      active = false;
    };
  }, [customerId]);

  useEffect(() => {
    if (!isEdit || !assetId) return undefined;
    let active = true;
    setLoading(true);
    setFormWarning("");
    customerService.getAssetById(assetId).then((detail) => {
      if (!active) return;
      const mapped = mapAssetFromApi(detail || {});
      const nextCustomerId = mapped.customerId || searchParams.get("customerId") || "";
      setAssets([mapped]);
      if (nextCustomerId) setCustomerId(String(nextCustomerId));
    }).catch((err) => {
      console.error("Failed to load asset:", err);
      if (active) setFormWarning(err?.message || i18n.t("crm.failedToLoadCustomerAssets"));
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [assetId, isEdit, searchParams]);

  const savedLocations = locations.filter((location) => locationRecordId(location));
  const defaultLocationId = locationRecordId(savedLocations[0]) || "";

  const updateAsset = (index, patch) => {
    setAssets((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addAsset = () => {
    setAssets((prev) => [...prev, emptyAsset(defaultLocationId)]);
  };

  const removeAsset = (index) => {
    setAssets((prev) => {
      const remaining = prev.filter((_, i) => i !== index);
      return remaining.length > 0 ? remaining : [emptyAsset(defaultLocationId)];
    });
  };

  const handleCustomerChange = (nextId) => {
    setCustomerId(nextId);
    setFormWarning("");
    const match = customers.find((customer) => String(customerRecordId(customer)) === String(nextId));
    setSelectedCustomer(match || null);
    setCustomerName(match ? customerLabel(match) : "");
    setAssets([emptyAsset()]);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (saving) return;
    if (!customerId) {
      setFormWarning(i18n.t("crm.selectCustomerBeforeSavingAssets"));
      return;
    }
    if (savedLocations.length === 0) {
      setFormWarning(i18n.t("crm.pleaseSaveAtLeastOneLocationBeforeAddingAssets"));
      return;
    }
    if (assets.some(assetMissingRequired)) {
      setFormWarning(i18n.t("crm.eachAssetRequiresLocationAssetNameAndCategory"));
      return;
    }

    setSaving(true);
    setFormWarning("");
    try {
      await Promise.all(
        assets.map((asset) => {
          const payload = buildAssetPayload(customerId, asset);
          const id = assetRecordId(asset);
          if (id) return customerService.updateAsset(id, payload);
          return customerService.createAsset(payload);
        })
      );
      navigate(`${ASSETS_LIST_PATH}?customerId=${encodeURIComponent(customerId)}`);
    } catch (err) {
      console.error("Save assets failed:", err);
      const errMsg = err?.data?.assetName || err?.data?.locationId || err?.message || i18n.t("crm.failedToSaveAssets");
      setFormWarning(String(errMsg));
      setSaving(false);
    }
  };

  const customerOptions = withSelectedCustomer(customers, selectedCustomer);

  return (
    <AppLayout>
      <form className="space-y-6" onSubmit={handleSave}>
        <PageHeader
          back={<PageBackLink to={listPath} />}
          title={isEdit ? i18n.t("crm.editAsset") : i18n.t("formActions.addAsset")}
        />

        {formWarning && <AlertBanner type="error">{formWarning}</AlertBanner>}

        <div className="bg-white rounded-2xl border border-white/60 shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] p-4 sm:p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <FieldLabel className="text-[11px] font-bold per-file-hint" i18nKey="crm.selectCustomer" required />
              {isEdit ? (
                <input readOnly value={customerName || customerId} className={fieldClass} />
              ) : (
                <FormSelect required value={customerId} onChange={(e) => handleCustomerChange(e.target.value)} className={selectClass}>
                  <option value="">{i18n.t("crm.selectCustomer")}</option>
                  {customerOptions.map((customer) => {
                    const id = customerRecordId(customer);
                    return (
                      <option key={id} value={String(id)}>{customerLabel(customer)}</option>
                    );
                  })}
                </FormSelect>
              )}
            </div>
          </div>

          {customerId && savedLocations.length === 0 && !loading && (
            <p className="text-xs text-error">
              <Trans i18nKey="formActions.saveAtLeastOneLocationBeforeAddingAssets" />
            </p>
          )}

          {loading ? (
            <div className="h-24 rounded-xl bg-workspace/50 animate-pulse" />
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-base font-bold text-foreground">
                  {i18n.t("crm.customerAssets")} ({assets.length})
                </h3>
                {!isEdit && (
                  <Button
                    type="button"
                    onClick={addAsset}
                    disabled={savedLocations.length === 0}
                    className="h-9 px-3 rounded-xl bg-primary text-white text-xs hover:bg-accent disabled:opacity-50"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    <Trans i18nKey="formActions.addAsset" />
                  </Button>
                )}
              </div>

              {assets.map((asset, index) => (
                <AssetFields
                  key={assetRecordId(asset) || `new-asset-${index}`}
                  asset={asset}
                  index={index}
                  locations={savedLocations}
                  categoryOptions={categoryOptions}
                  lookupsLoading={lookupsLoading}
                  canRemove={!isEdit && assets.length > 1}
                  onChange={(patch) => updateAsset(index, patch)}
                  onRemove={() => removeAsset(index)}
                />
              ))}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-(--color-text-border)/50">
            <Button
              type="button"
              onClick={() => navigate(listPath)}
              className="h-10 px-4 rounded-xl border border-(--color-text-border) bg-white hover:bg-workspace text-foreground font-semibold text-sm"
            >
              <Trans i18nKey="formActions.cancel" />
            </Button>
            <Button
              type="submit"
              disabled={saving || loading}
              className="h-10 px-5 rounded-xl bg-primary text-white hover:bg-accent font-semibold text-sm disabled:opacity-50"
            >
              {saving ? i18n.t("inventory.saving") : i18n.t("formActions.save")}
            </Button>
          </div>
        </div>
      </form>
    </AppLayout>
  );
}
